const COMPONENT_TYPES = {
  IMAGE: 1,
  NUMBER: 2,
  TEXT: 3,
  BOOLEAN: 4,
  SUBMODULE: 6
};

const extractImageId = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object' && raw.$oid) return raw.$oid;
  return null;
};

/**
 * Builds a lookup from the top-level photos array, grouped by field name.
 * Each entry is { imageId, photoType }.
 */
const buildPhotosMap = (photos) => {
  const map = {};
  if (!Array.isArray(photos)) return map;

  for (const photo of photos) {
    if (!photo || typeof photo !== 'object') continue;

    const imageId = extractImageId(photo.image);
    if (!imageId) continue;

    const field = typeof photo.field === 'string' && photo.field.length > 0 ? photo.field : 'photos';
    if (!map[field]) map[field] = [];
    map[field].push({ imageId, photoType: photo.photo_type });
  }

  return map;
};

export const resolveSchemaId = (surveyResponse) => {
  const raw = surveyResponse?.survey_schema;
  if (!raw) return null;

  if (typeof raw === 'string') return raw;
  if (raw.$oid) return raw.$oid;
  if (raw._id?.$oid) return raw._id.$oid;
  if (raw._id) return typeof raw._id === 'string' ? raw._id : null;

  return null;
};

const parseSurveyData = (screens, fields, photos) => {
  const result = [];

  const safeScreens = Array.isArray(screens) ? screens : [];
  const safeFields = fields && typeof fields === 'object' ? fields : {};
  const photosMap = buildPhotosMap(photos);

  for (const screen of safeScreens) {
    if (!screen || !Array.isArray(screen.components)) continue;

    for (const component of screen.components) {
      if (!component || typeof component !== 'object') continue;

      const { field_name, label, component_type, children } = component;
      if (!field_name) continue;

      // Image components: look up in photosMap
      if (component_type === COMPONENT_TYPES.IMAGE) {
        const entries = photosMap[field_name];
        if (entries && entries.length > 0) {
          result.push({ field_name, label, type: 'image', value: entries.map(e => e.imageId) });
        }
        continue;
      }

      const value = safeFields[field_name];
      if (value === undefined || value === null) continue;

      if (component_type === COMPONENT_TYPES.SUBMODULE && Array.isArray(value)) {
        const childComponents = Array.isArray(children) ? children : [];

        const items = value.map((element, i) => {
          const parsed = { fields: [], photos: [] };

          for (const child of childComponents) {
            if (!child || typeof child !== 'object' || !child.field_name) continue;

            if (child.component_type === COMPONENT_TYPES.IMAGE) {
              // Photos for submodule items live in the top-level photos array
              // under the naming convention `{field_name}_{index}_{child_field_name}`
              const lookupKey = `${field_name}_${i}_${child.field_name}`;
              const entries = photosMap[lookupKey];
              if (entries && entries.length > 0) {
                for (const entry of entries) {
                  parsed.photos.push({ field: child.field_name, imageId: entry.imageId, photoType: entry.photoType });
                }
              }
              continue;
            }

            if (!element || typeof element !== 'object') continue;
            const childValue = element[child.field_name];
            if (childValue === undefined || childValue === null) continue;
            parsed.fields.push({ field_name: child.field_name, label: child.label, type: 'value', value: childValue });
          }

          return parsed;
        });

        result.push({ field_name, label, type: 'submodule', value: items });
      }
      else {
        result.push({ field_name, label, type: 'value', value });
      }
    }
  }

  return result;
};

export default parseSurveyData;
